import type {
  AttributeConfigType,
  CategoryStatus,
  DiscountCodeType,
  PageSettingCode,
  PaymentGatewayDiscountType,
  ProductAmountType,
  ProductCommentStatus,
  ProductPriceType,
  ProductStatus,
  ProductType,
  ProductVariantType,
  PurchaseStatus,
  RoleScopeAction,
  RoleScopeType,
  TagStatus,
  WatermarkPosition,
} from 'types';

// NOTE: The AdminPageBuilder settings are keyed by dynamic codes. The known
// landing codes are mapped to a friendly Persian label, while any unknown code
// gracefully falls back to its raw key.
const PAGE_SETTING_CODE_LABELS: Record<string, string> = {
  Landing_Pos1_Right_Banner: 'جایگاه ۱، موقعیت راست',
  Landing_Pos1_Top_Banner: 'جایگاه ۱، موقعیت بالا',
  Landing_Pos1_Bottom_Banner: 'جایگاه ۱، موقعیت پایین',
  Landing_Pos2_CategoryProduct: 'جایگاه ۱',
  Landing_Pos3_TopRight_Banner: 'جایگاه ۲، موقعیت بالا راست',
  Landing_Pos3_BottomRight_Banner: 'جایگاه ۲، موقعیت پایین راست',
  Landing_Pos3_Mid_Banner: 'جایگاه ۲، موقعیت میانه',
  Landing_Pos3_TopLeft_Banner: 'جایگاه ۲، موقعیت بالا چپ',
  Landing_Pos3_BottomLeft_Banner: 'جایگاه ۲، موقعیت پایین چپ',
  Landing_Pos4_CategoryProduct: 'جایگاه ۲',
  Landing_Pos5_Right_Banner: 'جایگاه ۳، موقعیت راست',
  Landing_Pos5_Left_Banner: 'جایگاه ۳، موقعیت چپ',
  Landing_Pos6_CategoryProduct: 'جایگاه ۳',
  Landing_Pos7_TopRight_Banner: 'جایگاه ۴، موقعیت بالا راست',
  Landing_Pos7_BottomRight_Banner: 'جایگاه ۴، موقعیت پایین راست',
  Landing_Pos7_TopLeft_Banner: 'جایگاه ۴، موقعیت بالا چپ',
  Landing_Pos7_BottomLeft_Banner: 'جایگاه ۴، موقعیت پایین چپ',
  Landing_Pos8_CategoryProduct: 'جایگاه ۴',
};

const localizePageSettingCode = (code?: PageSettingCode) => ({
  label: (code && PAGE_SETTING_CODE_LABELS[code]) || code || '',
});

const localizePurchaseStatus = (status?: PurchaseStatus) => {
  switch (status) {
    case 'Purchased':
      return { label: 'در حال انجام', color: 'yellow' } as const;
    case 'Posted':
      return { label: 'ارسال شده', color: 'blue' } as const;
    case 'Delivered':
      return { label: 'تحویل داده شده', color: 'green' } as const;
    case 'Cancelled':
      return { label: 'لغو شده', color: 'red' } as const;
    default:
      return { label: undefined, color: undefined } as const;
  }
};

const localizeCategoryStatus = (status?: CategoryStatus) => {
  switch (status) {
    case 'Active':
      return { label: 'فعال', color: 'green' } as const;
    case 'DeActive':
      return { label: 'غیرفعال', color: 'red' } as const;
    default:
      return { label: undefined, color: undefined } as const;
  }
};

const localizeTagStatus = (status?: TagStatus) => {
  switch (status) {
    case 'Active':
      return { label: 'فعال', color: 'green' } as const;
    case 'DeActive':
      return { label: 'غیرفعال', color: 'red' } as const;
    default:
      return { label: undefined, color: undefined } as const;
  }
};

const localizeProductStatus = (status?: ProductStatus) => {
  switch (status) {
    case 'Draft':
      return { label: 'پیش‌نویس', color: 'yellow' } as const;
    case 'Private':
      return { label: 'خصوصی', color: 'purple' } as const;
    case 'Submitted':
      return { label: 'منتشرشده', color: 'green' } as const;
    default:
      return { label: undefined, color: undefined } as const;
  }
};

const localizeProductCommentStatus = (status?: ProductCommentStatus) => {
  switch (status) {
    case 'OnReview':
      return { label: 'در حال بررسی', color: 'yellow' } as const;
    case 'Rejected':
      return { label: 'ردشده', color: 'red' } as const;
    case 'Accepted':
      return { label: 'تاییدشده', color: 'green' } as const;
    default:
      return { label: undefined, color: undefined } as const;
  }
};

const localizeAttributeType = (type?: AttributeConfigType) => {
  switch (type) {
    case 'Text':
      return { label: 'متن' } as const;
    case 'Number':
      return { label: 'عدد' } as const;
    case 'Select':
      return { label: 'تک‌گزینه‌ایی' } as const;
    case 'CheckBox':
      return { label: 'چندگزینه‌ایی' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeProductType = (type?: ProductType) => {
  switch (type) {
    case 'Normal':
      return { label: 'ثابت' } as const;
    case 'Variable':
      return { label: 'متغیر' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeProductVariantType = (type?: ProductVariantType) => {
  switch (type) {
    case 'File':
      return { label: 'فایل' } as const;
    case 'Text':
      return { label: 'متن' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeProductAmountType = (type?: ProductAmountType) => {
  switch (type) {
    case 'Increase':
      return { label: '(+) افزایش' } as const;
    case 'Decrease':
      return { label: '(-) کاهش' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeProductPriceType = (type?: ProductPriceType) => {
  switch (type) {
    case 'Percentage':
      return { label: 'درصد' } as const;
    case 'Price':
      return { label: 'تومان' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeDiscountCodeType = (type?: DiscountCodeType) => {
  switch (type) {
    case 'Percentage':
      return { label: 'درصد' } as const;
    case 'Price':
      return { label: 'تومان' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeShippingAmountType = (type?: DiscountCodeType) => {
  switch (type) {
    case 'Percentage':
      return { label: 'درصد' } as const;
    case 'Price':
      return { label: 'تومان' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizePaymentGatewayDiscountType = (
  type?: PaymentGatewayDiscountType,
) => {
  switch (type) {
    case 'Percentage':
      return { label: 'درصد' } as const;
    case 'Price':
      return { label: 'تومان' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeRoleScopeType = (type?: RoleScopeType) => {
  switch (type) {
    case 'tag':
      return { label: 'برچسب‌ها' } as const;
    case 'user':
      return { label: 'کاربرها' } as const;
    case 'brand':
      return { label: 'برند‌ها' } as const;
    case 'config':
      return { label: 'تنظیمات قالب' } as const;
    case 'product':
      return { label: 'محصول‌ها' } as const;
    case 'catalog':
      return { label: 'کاتالوگ‌ها' } as const;
    case 'category':
      return { label: 'دسته‌بندی‌ها' } as const;
    case 'purchase':
      return { label: 'سفارش‌ها' } as const;
    case 'discount':
      return { label: 'کد تخفیف‌ها' } as const;
    case 'special-offer':
      return { label: 'تخفیف‌های ویژه' } as const;
    case 'payment-gateway':
      return { label: 'درگاه‌های پرداخت' } as const;
    case 'product-comment':
      return { label: 'دیدگاه کاربرها' } as const;
    case 'packaging-model':
      return { label: 'بسته‌بندی‌ها' } as const;
    case 'attribute-config':
      return { label: 'فیلد‌ها' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeRoleScopeAction = (action?: RoleScopeAction) => {
  switch (action) {
    case 'read':
      return { label: 'مشاهده' } as const;
    case 'create':
      return { label: 'افزودن' } as const;
    case 'modify':
      return { label: 'ویرایش' } as const;
    case 'delete':
      return { label: 'حذف' } as const;
    case 'publish':
      return { label: 'انتشار' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeWatermarkPosition = (position?: WatermarkPosition) => {
  switch (position) {
    case 'top':
      return { label: 'بالا' } as const;
    case 'right':
      return { label: 'راست' } as const;
    case 'bottom':
      return { label: 'پایین' } as const;
    case 'left':
      return { label: 'چپ' } as const;
    default:
      return { label: undefined } as const;
  }
};

const localizeSubProductActivateStock = (activateStock?: boolean) => {
  switch (activateStock) {
    case true:
      return { label: 'فعال', color: 'green' } as const;
    default:
      return { label: 'غیرفعال', color: 'red' } as const;
  }
};

const localizeSubProductActiveSnapPay = (activeSnapPay?: boolean) => {
  switch (activeSnapPay) {
    case true:
      return { label: 'فعال', color: 'green' } as const;
    default:
      return { label: 'غیرفعال', color: 'red' } as const;
  }
};

const localizeSubProductActiveTorobPay = (activeTorobPay?: boolean) => {
  switch (activeTorobPay) {
    case true:
      return { label: 'فعال', color: 'green' } as const;
    default:
      return { label: 'غیرفعال', color: 'red' } as const;
  }
};

const localizeSubProductFreeShipping = (freeShipping?: boolean) => {
  switch (freeShipping) {
    case true:
      return { label: 'فعال', color: 'green' } as const;
    default:
      return { label: 'غیرفعال', color: 'red' } as const;
  }
};

const localizeSubProductExpressShipping = (expressShipping?: boolean) => {
  switch (expressShipping) {
    case true:
      return { label: 'فعال', color: 'green' } as const;
    default:
      return { label: 'غیرفعال', color: 'red' } as const;
  }
};

const localizeDiscountCodeEnable = (enable?: boolean) => {
  switch (enable) {
    case true:
      return { label: 'فعال', color: 'green' } as const;
    default:
      return { label: 'غیرفعال', color: 'red' } as const;
  }
};

export {
  localizeAttributeType,
  localizeCategoryStatus,
  localizeDiscountCodeEnable,
  localizeDiscountCodeType,
  localizePageSettingCode,
  localizePaymentGatewayDiscountType,
  localizeProductAmountType,
  localizeProductCommentStatus,
  localizeProductPriceType,
  localizeProductStatus,
  localizeProductType,
  localizeProductVariantType,
  localizePurchaseStatus,
  localizeRoleScopeAction,
  localizeRoleScopeType,
  localizeShippingAmountType,
  localizeSubProductActivateStock,
  localizeSubProductActiveSnapPay,
  localizeSubProductActiveTorobPay,
  localizeSubProductExpressShipping,
  localizeSubProductFreeShipping,
  localizeTagStatus,
  localizeWatermarkPosition,
};
